import React from 'react'
import { Link } from 'react-router-dom'
import {
  Badge,
  Box,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  NumberInput,
  NumberInputField,
  Radio,
  RadioGroup,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea
} from '@chakra-ui/react'

import { agreementSigners } from '@homevest/utils'
import { addDays } from 'date-fns'
import { RENEWAL_TERM_OPTIONS } from 'constants/renewal-terms'
import { formatDate, parseDate } from 'lib/date-time'
import { formatMoney } from 'lib/numbers'
import { combineFirstNameLastName } from 'lib/utils'
import { AgreementSigners } from 'types/FullRental'
import {
  isValidCustomRenewalTerm,
  isValidRenewalRent,
  isValidRenewalTerm
} from './renewalFormValidation'

const { ROLES } = agreementSigners

const FormSection = ({
  children,
  description,
  title
}: React.PropsWithChildren<{ description?: string; title: string }>) => (
  <Box bg='white' borderColor='gray.200' borderRadius='lg' borderWidth='1px' p={5}>
    <Text fontSize='lg' fontWeight='semibold'>
      {title}
    </Text>
    {description && (
      <Text color='gray.600' fontSize='sm' mt={1}>
        {description}
      </Text>
    )}
    <Box mt={4}>{children}</Box>
  </Box>
)

const MoneyField = ({
  error,
  helperText,
  isRequired,
  label,
  min = 0,
  onChange,
  value
}: {
  error?: string
  helperText?: string
  isRequired?: boolean
  label: string
  min?: number
  onChange: (value: number | null) => void
  value: number | null
}) => (
  <FormControl isInvalid={Boolean(error)} isRequired={isRequired}>
    <FormLabel>{label}</FormLabel>
    <InputGroup>
      <InputLeftElement pointerEvents='none' color='gray.500'>
        $
      </InputLeftElement>
      <NumberInput
        clampValueOnBlur={false}
        min={min}
        precision={2}
        step={0.01}
        value={value !== null && Number.isFinite(value) ? value : ''}
        width='full'
        onChange={(_, nextValue) => onChange(Number.isFinite(nextValue) ? nextValue : null)}
      >
        <NumberInputField paddingLeft={8} placeholder='0.00' />
      </NumberInput>
    </InputGroup>
    {error ? (
      <FormErrorMessage>{error}</FormErrorMessage>
    ) : helperText ? (
      <FormHelperText>{helperText}</FormHelperText>
    ) : null}
  </FormControl>
)

export const getRentChangeDescription = (rent: number, previousRent: number) => {
  if (!Number.isFinite(rent) || !Number.isFinite(previousRent) || previousRent <= 0) {
    return undefined
  }

  const percentageChange = ((rent - previousRent) / previousRent) * 100
  const formattedPercentage = Math.abs(percentageChange).toLocaleString(undefined, {
    maximumFractionDigits: 1
  })
  const direction = rent > previousRent ? 'increase' : rent < previousRent ? 'decrease' : 'change'

  return `${formattedPercentage}% ${direction} from previous rent of ${formatMoney(
    previousRent,
    2,
    '$'
  )}.`
}

type RenewRentalModalFormProps = {
  rent: number
  setRent: (rent: number) => void
  previousRent: number
  optionPremium: number
  setOptionPremium: (rent: number) => void
  leaseVariant: 'standard' | 'kcpoa'
  setLeaseVariant: (variant: 'standard' | 'kcpoa') => void
  isKCPOAEligible: boolean
  initialOptionFee: number
  setInitialOptionFee: (amount: number) => void
  monthlyOptionFee: number
  setMonthlyOptionFee: (amount: number) => void
  depositCredit: number
  setDepositCredit: (amount: number) => void
  purchasePrice: number | null
  setPurchasePrice: (amount: number | null) => void
  term: number
  setTerm: (term: number) => void
  notes: string
  setNotes: (notes: string) => void
  startDate: Date
  endDate: Date | null
  setEndDate?: (date: Date | null) => void
  showOtherTerm: boolean
  setShowOtherTerm: (show: boolean) => void
  agreementSigners: AgreementSigners
}

const RenewRentalModalForm = ({
  rent,
  setRent,
  previousRent,
  optionPremium,
  setOptionPremium,
  leaseVariant,
  setLeaseVariant,
  isKCPOAEligible,
  initialOptionFee,
  setInitialOptionFee,
  monthlyOptionFee,
  setMonthlyOptionFee,
  depositCredit,
  setDepositCredit,
  purchasePrice,
  setPurchasePrice,
  term,
  setTerm,
  notes,
  setNotes,
  startDate,
  endDate,
  setEndDate = () => undefined,
  showOtherTerm,
  setShowOtherTerm,
  agreementSigners
}: RenewRentalModalFormProps) => {
  const isKCPOA = leaseVariant === 'kcpoa'
  const usesCustomEndDate = showOtherTerm && !isKCPOA
  const minimumTerm = isKCPOA ? 3 : usesCustomEndDate ? 2 : 1
  const minimumTermUnit = minimumTerm === 1 ? 'month' : 'months'
  const hasValidRenewalTerm = usesCustomEndDate
    ? isValidCustomRenewalTerm(term)
    : isValidRenewalTerm(term, leaseVariant)
  const termError = !hasValidRenewalTerm
    ? usesCustomEndDate
      ? 'Choose an end date that creates a term of at least 2 months.'
      : `Enter a whole number of at least ${minimumTerm} ${minimumTermUnit}.`
    : undefined
  const rentError = !isValidRenewalRent(rent)
    ? 'Monthly rent must be greater than $0.00.'
    : undefined
  const depositCreditError =
    depositCredit > initialOptionFee
      ? 'Deposit credit cannot exceed the initial option fee.'
      : undefined
  const purchasePriceError =
    purchasePrice !== null && purchasePrice <= 0
      ? 'Purchase price must be greater than $0.00.'
      : undefined

  const handleLeaseVariantChange = (nextVariant: string) => {
    const variant = nextVariant as 'standard' | 'kcpoa'
    setLeaseVariant(variant)
    if (variant === 'standard' && showOtherTerm) {
      setEndDate(endDate)
    }
    if (variant === 'kcpoa' && !isValidRenewalTerm(term, 'kcpoa')) {
      setTerm(12)
      setShowOtherTerm(false)
    }
  }

  return (
    <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={5} alignItems='start'>
      <Stack spacing={5}>
        <FormSection
          title='Renewal terms'
          description='Choose the lease template and the exact renewal period.'
        >
          <Stack spacing={4}>
            <FormControl>
              <FormLabel>Lease type</FormLabel>
              <RadioGroup value={leaseVariant} onChange={handleLeaseVariantChange}>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                  <Box
                    as='label'
                    borderColor={leaseVariant === 'standard' ? 'teal.400' : 'gray.200'}
                    borderRadius='md'
                    borderWidth='1px'
                    cursor='pointer'
                    p={4}
                    bg={leaseVariant === 'standard' ? 'teal.50' : 'white'}
                  >
                    <HStack align='start'>
                      <Radio value='standard' mt={1} />
                      <Box>
                        <Text fontWeight='semibold'>Standard</Text>
                        <Text color='gray.600' fontSize='sm' mt={1}>
                          Uses the active standard lease template.
                        </Text>
                      </Box>
                    </HStack>
                  </Box>
                  <Box
                    as='label'
                    borderColor={leaseVariant === 'kcpoa' ? 'teal.400' : 'gray.200'}
                    borderRadius='md'
                    borderWidth='1px'
                    cursor={isKCPOAEligible ? 'pointer' : 'not-allowed'}
                    opacity={isKCPOAEligible ? 1 : 0.55}
                    p={4}
                    bg={leaseVariant === 'kcpoa' ? 'teal.50' : 'white'}
                  >
                    <HStack align='start'>
                      <Radio value='kcpoa' isDisabled={!isKCPOAEligible} mt={1} />
                      <Box>
                        <HStack spacing={2}>
                          <Text fontWeight='semibold' whiteSpace='nowrap'>
                            RPP
                          </Text>
                          {!isKCPOAEligible && <Badge colorScheme='gray'>Not eligible</Badge>}
                        </HStack>
                        <Text color='gray.600' fontSize='sm' mt={1}>
                          Uses the active RPP template and purchase-option terms.
                        </Text>
                      </Box>
                    </HStack>
                  </Box>
                </SimpleGrid>
              </RadioGroup>
            </FormControl>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl>
                <FormLabel>Renewal term</FormLabel>
                <Select
                  value={showOtherTerm ? '0' : String(term)}
                  onChange={(event) => {
                    const nextTerm = Number(event.currentTarget.value)
                    if (nextTerm === 0) {
                      if (!isKCPOA) setEndDate(endDate)
                      setShowOtherTerm(true)
                      if (!isValidRenewalTerm(term, leaseVariant)) {
                        setTerm(minimumTerm)
                      }
                      return
                    }
                    setShowOtherTerm(false)
                    setTerm(nextTerm)
                  }}
                >
                  {RENEWAL_TERM_OPTIONS.filter(
                    ({ value }) => !isKCPOA || value === 0 || value >= 3
                  ).map(({ label, value }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </FormControl>

              {showOtherTerm && (
                <FormControl id='custom-renewal-term' isInvalid={Boolean(termError)} isRequired>
                  <FormLabel>{usesCustomEndDate ? 'Lease term' : 'Custom term'}</FormLabel>
                  <InputGroup>
                    {usesCustomEndDate ? (
                      <Input
                        aria-label='Lease term'
                        bg='gray.50'
                        id='custom-renewal-term'
                        isReadOnly
                        paddingRight={20}
                        value={Number.isFinite(term) ? term : ''}
                      />
                    ) : (
                      <NumberInput
                        clampValueOnBlur={false}
                        min={minimumTerm}
                        step={1}
                        value={Number.isFinite(term) ? term : ''}
                        width='full'
                        onChange={(_, value) =>
                          setTerm(Number.isFinite(value) ? value : Number.NaN)
                        }
                      >
                        <NumberInputField
                          aria-label='Custom term'
                          id='custom-renewal-term'
                          paddingRight={20}
                        />
                      </NumberInput>
                    )}
                    <InputRightElement pointerEvents='none' color='gray.500' width={20}>
                      months
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>{termError}</FormErrorMessage>
                  {usesCustomEndDate && (
                    <FormHelperText>Calculated from the selected end date.</FormHelperText>
                  )}
                </FormControl>
              )}
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl>
                <FormLabel>Start date</FormLabel>
                <Input bg='gray.50' isReadOnly type='date' value={formatDate(startDate)} />
              </FormControl>
              <FormControl
                isInvalid={usesCustomEndDate && (!endDate || endDate <= startDate)}
                isRequired={usesCustomEndDate}
              >
                <FormLabel>End date</FormLabel>
                <Input
                  aria-label='End date'
                  bg={usesCustomEndDate ? 'white' : 'gray.50'}
                  isReadOnly={!usesCustomEndDate}
                  min={formatDate(addDays(startDate, 1))}
                  type='date'
                  value={endDate ? formatDate(endDate) : ''}
                  onChange={(event) =>
                    setEndDate(
                      event.currentTarget.value ? parseDate(event.currentTarget.value) : null
                    )
                  }
                />
                <FormErrorMessage>
                  {endDate ? 'End date must be after the start date.' : 'Select an end date.'}
                </FormErrorMessage>
              </FormControl>
              <Text color='gray.600' fontSize='sm' gridColumn={{ md: '1 / -1' }}>
                {usesCustomEndDate
                  ? 'Choose the lease end date. The term is calculated automatically.'
                  : 'Dates follow the current lease and selected term automatically.'}
              </Text>
            </SimpleGrid>
          </Stack>
        </FormSection>

        <FormSection
          title='Renewal note'
          description='Add optional context for the renewal record.'
        >
          <Textarea
            minH='140px'
            placeholder='Add a note'
            value={notes}
            onChange={(event) => setNotes(event.currentTarget.value)}
          />
        </FormSection>
      </Stack>

      <Stack spacing={5}>
        <FormSection
          title='Signers'
          description='All active rental users and any cosigner from the current lease will receive and sign the renewal. Update the rental users before issuing if the tenant list needs to change.'
        >
          <Stack spacing={3}>
            {!agreementSigners.some((signer) => signer.role === ROLES.PRIMARY_TENANT) && (
              <Box
                bg='orange.50'
                borderColor='orange.200'
                borderRadius='md'
                borderWidth='1px'
                p={3}
              >
                <Text color='orange.800' fontSize='sm'>
                  Add an active primary rental user before reviewing this renewal.
                </Text>
              </Box>
            )}
            {agreementSigners.map(({ user, role }) => {
              if (!user) return null
              const isPrimary = role === ROLES.PRIMARY_TENANT
              const isCosigner = role === ROLES.COSIGNER
              return (
                <FlexSigner
                  key={user.id}
                  isCosigner={isCosigner}
                  isPrimary={isPrimary}
                  name={combineFirstNameLastName(user)}
                  userId={user.id}
                />
              )
            })}
          </Stack>
        </FormSection>

        <FormSection
          title='Pricing'
          description={
            isKCPOA
              ? 'Confirm the rent and purchase-option amounts shown in the renewal.'
              : 'Confirm the monthly rent and option premium.'
          }
        >
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <MoneyField
              error={rentError}
              helperText={getRentChangeDescription(rent, previousRent)}
              label='Monthly rent'
              min={0.01}
              onChange={(value) => setRent(value ?? Number.NaN)}
              value={rent}
            />
            {!isKCPOA && (
              <MoneyField
                label='Monthly option premium'
                onChange={(value) => setOptionPremium(value ?? Number.NaN)}
                value={optionPremium}
              />
            )}
            {isKCPOA && (
              <>
                <MoneyField
                  label='Initial option fee'
                  onChange={(value) => setInitialOptionFee(value ?? Number.NaN)}
                  value={initialOptionFee}
                />
                <MoneyField
                  label='Monthly option fee'
                  onChange={(value) => setMonthlyOptionFee(value ?? Number.NaN)}
                  value={monthlyOptionFee}
                />
                <MoneyField
                  error={depositCreditError}
                  helperText='Applied against the initial option fee.'
                  label='Deposit credit'
                  onChange={(value) => setDepositCredit(value ?? Number.NaN)}
                  value={depositCredit}
                />
                <MoneyField
                  error={purchasePriceError}
                  helperText='Required for RPP renewals.'
                  isRequired
                  label='Purchase price'
                  min={0.01}
                  onChange={setPurchasePrice}
                  value={purchasePrice}
                />
              </>
            )}
          </SimpleGrid>
        </FormSection>
      </Stack>
    </SimpleGrid>
  )
}

const FlexSigner = ({
  isCosigner,
  isPrimary,
  name,
  userId
}: {
  isCosigner: boolean
  isPrimary: boolean
  name: string
  userId: string
}) => (
  <HStack borderColor='gray.200' borderRadius='md' borderWidth='1px' justify='space-between' p={3}>
    <Box>
      <HStack spacing={2}>
        <Link to={`/users/${userId}`} target='_blank' rel='noopener noreferrer'>
          {name}
        </Link>
        {isPrimary && <Badge colorScheme='green'>Primary</Badge>}
      </HStack>
      <Text color='gray.500' fontSize='xs'>
        {isCosigner ? 'Cosigner from current lease' : 'Active rental user'}
      </Text>
    </Box>
  </HStack>
)

export default RenewRentalModalForm
